import { BooleanInput } from '@angular/cdk/coercion';
import { HttpClient } from '@angular/common/http';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { WorkspaceTabsService } from 'app/layout/common/workspace-tabs/workspace-tabs.service';
import { User } from 'app/core/user/user.types';
import { AppCommandsService } from 'app/layout/common/command-palette/shared/app-commands.service';
import { buildAccountPaletteCommands } from 'app/layout/common/command-palette/app/app-commands';
import { ChangePasswordService } from 'app/shared/services/change-password.service';
import { ImportDataService } from 'app/shared/services/import-data.service';
import { SignOutConfirmationService } from 'app/shared/services/sign-out-confirmation.service';
import { environment } from 'environments/environment';
import { Subject, take, takeUntil } from 'rxjs';

/** Shape of /qa-version-info.json published by QA deploy workflows */
interface QaVersionInfo {
  frontend?: { version?: string; deployNumber?: number };
  backend?: { version?: string; deployNumber?: number };
  combined?: { releaseName?: string; releaseNumber?: number };
}

@Component({
  selector: 'user',
  templateUrl: './user.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'user',
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    NgClass,
    MatDividerModule,
    MatSnackBarModule,
  ],
})
export class UserComponent implements OnInit, OnDestroy {
  /* eslint-disable @typescript-eslint/naming-convention */
  static ngAcceptInputType_showAvatar: BooleanInput;
  /* eslint-enable @typescript-eslint/naming-convention */

  @Input() showAvatar: boolean = true;
  user: User;
  qaVersionDisplayText: string = this._buildQaVersionDisplayText();

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _unregisterAppCommands?: () => void;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _router: Router,
    private _userService: UserService,
    private _changePasswordService: ChangePasswordService,
    private _importDataService: ImportDataService,
    private _httpClient: HttpClient,
    private _appCommands: AppCommandsService,
    private _signOutConfirmation: SignOutConfirmationService,
    private _workspaceTabs: WorkspaceTabsService,
  ) {}

  ngOnInit(): void {
    this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
      this.user = user;
      this._changeDetectorRef.markForCheck();
    });

    this._loadQaVersionInfo();

    this._unregisterAppCommands = this._appCommands.register({
      build: () =>
        buildAccountPaletteCommands({
          profile: () => this.navigateToProfile(),
          settings: () => this.navigateToSettings(),
          importData: () => this.onImportDataClick(),
          changePassword: () => this.onChangePasswordClick(),
          loginHistory: () => this.navigateToLoginHistory(),
          signOut: () => this.signOut(),
        }),
    });
  }

  ngOnDestroy(): void {
    this._unregisterAppCommands?.();
    this._unregisterAppCommands = undefined;
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  updateUserStatus(status: string): void {
    if (!this.user) {
      return;
    }

    this._userService
      .update({
        ...this.user,
        status,
      })
      .subscribe();
  }

  navigateToProfile(): void {
    this._router.navigate(['/user-profile']);
  }

  navigateToSettings(): void {
    this._router.navigate(['/settings']);
  }

  onImportDataClick(): void {
    this._importDataService.openImportPanel();
  }

  navigateToPricing(): void {
    this._router.navigate(['/pricing']);
  }

  navigateToLoginHistory(): void {
    this._router.navigate(['/access-control/login-history']);
  }

  onChangePasswordClick(): void {
    this._changePasswordService.initiateChangePassword();
  }

  signOut(): void {
    this._signOutConfirmation
      .confirm()
      .pipe(take(1))
      .subscribe((canSignOut) => {
        if (canSignOut) {
          this._router.navigate(['/sign-out']);
        }
      });
    void this._workspaceTabs.confirmSignOut().then((leave) => {
      if (!leave) {
        return;
      }
      this._router.navigate(['/sign-out']);
    });
  }

  private _loadQaVersionInfo(): void {
    this._httpClient
      .get<QaVersionInfo>(`/${environment.versionInfoFile}?t=${Date.now()}`)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (info) => {
          this.qaVersionDisplayText = this._buildQaVersionDisplayText(info);
          this._changeDetectorRef.markForCheck();
        },
        error: () => {
          this.qaVersionDisplayText = this._buildQaVersionDisplayText();
          this._changeDetectorRef.markForCheck();
        },
      });
  }

  private _buildQaVersionDisplayText(info?: QaVersionInfo): string {
    const combinedName =
      info?.combined?.releaseName || environment.combinedReleaseName || 'qa-combined-0';
    const frontendVersion = info?.frontend?.version || environment.version || 'dev';
    const backendVersion = info?.backend?.version || '—';

    return `Combined: ${combinedName} | Frontend: ${frontendVersion} | Backend: ${backendVersion}`;
  }
}
